import { components } from "@/lib/openapi";
import { client } from "@/queries/client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";

type SecurityLaneSchema = components["schemas"]["SecurityLaneSchema"];
type SecurityLaneCreateSchema =
  components["schemas"]["SecurityLaneCreateSchema"];
type SecurityLaneUpdateSchema =
  components["schemas"]["SecurityLaneUpdateSchema"];
type CheckpointSchema = components["schemas"]["CheckpointSchema"];

export const useSecurityLanes = (): UseQueryResult<SecurityLaneSchema[]> => {
  return useQuery({
    queryKey: ["securityLanes"],
    queryFn: async () => {
      const { data, error } = await client.get().GET("/api/security/lanes");
      if (data) return data;
      console.warn("Failed to fetch security lanes:", error);
      throw new Error("Failed to fetch security lanes");
    },
  });
};

export const useCheckpoints = (): UseQueryResult<CheckpointSchema[]> => {
  return useQuery({
    queryKey: ["checkpoints"],
    queryFn: async () => {
      const { data, error } = await client
        .get()
        .GET("/api/security/checkpoints");
      if (data) return data;
      console.warn("Failed to fetch checkpoints:", error);
      throw new Error("Failed to fetch checkpoints");
    },
  });
};

export const useCreateSecurityLane = () => {
  const queryClient = useQueryClient();

  return useMutation<SecurityLaneSchema, Error, SecurityLaneCreateSchema>({
    mutationFn: async (payload: SecurityLaneCreateSchema) => {
      const { data, error } = await client.get().POST("/api/security/lane", {
        body: payload,
      });

      if (error) {
        console.error("Failed to create security lane:", error);
        throw new Error("Failed to create security lane");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["securityLanes"] });
    },
  });
};

export const useUpdateSecurityLane = () => {
  const queryClient = useQueryClient();

  return useMutation<
    SecurityLaneSchema,
    Error,
    { id: number; data: SecurityLaneUpdateSchema }
  >({
    mutationFn: async ({ id, data }) => {
      const { data: responseData, error } = await client
        .get()
        .PUT("/api/security/lane/{lane_id}", {
          params: { path: { lane_id: id } },
          body: data,
        });

      if (error) {
        console.error("Failed to update security lane:", error);
        throw new Error("Failed to update security lane");
      }

      return responseData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["securityLanes"] });
    },
  });
};

export const useDeleteSecurityLane = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data, error } = await client
        .get()
        .DELETE("/api/security/lane/{lane_id}", {
          params: { path: { lane_id: id } },
        });

      if (error) {
        console.error("Failed to delete security lane:", error);
        throw new Error("Failed to delete security lane");
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["securityLanes"] });
    },
  });
};
